import { FC, Fragment, useContext } from "react";
import {
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
} from "@mui/material";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useLocation, useNavigate } from "react-router-dom";
import { UserProgramSection } from "../../content/types";
import { UserProgramContext } from "../../context/user-program/user-program";

type Props = {
  isMobile: boolean;
  setSelectedTab: (tab: "videos" | "documents" | "monthly-training") => void;
  open: boolean;
  onClose: () => void;
  section: UserProgramSection;
};

const uploadTitles = new Map([
  [
    "ernährung",
    "Ernährungsprotokoll, Diätversuche-Protokoll, Gewichtsnachweise, Medizinische Notwendigkeitsbescheinigung, MMK-Verkürzungsschreiben",
  ],
  ["sport", "Bewegungsprotokoll"],
]);

export const MenuDrawer: FC<Props> = ({
  setSelectedTab,
  isMobile,
  open,
  onClose,
  section,
}) => {
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const { role } = useContext(UserProgramContext);

  const segments = pathname.split("/");

  const segmentName = segments[2]
    ? decodeURIComponent(segments[2])?.toLocaleLowerCase()
    : "";
  const segmentUploadTitle =
    uploadTitles.get(segmentName) ?? "Dokumente hochladen";

  const roleIsPatientGewicht = role === "patient-gewicht";

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      sx={{
        "& .MuiDrawer-paper": {
          position: "absolute",
          boxSizing: "border-box",
          width: "240px",
        },
      }}
      ModalProps={{
        keepMounted: true,
      }}
      open={open}
      onClose={onClose}
    >
      <Toolbar />
      <Divider />
      {!roleIsPatientGewicht && (
        <>
          {section.title === "Ernährung" && role !== "clinic-preview" && (
            <Fragment>
              <Divider />
              <List>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: "initial",
                      px: 2.5,
                    }}
                    onClick={() => setSelectedTab("monthly-training")}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 3,
                        justifyContent: "center",
                      }}
                    >
                      <CalendarTodayIcon />
                    </ListItemIcon>
                    <ListItemText primary="Termin für monatliche Schulung buchen" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Fragment>
          )}

          {section.title !== "Sport" && section.title !== "Verhaltenstraining" && (
            <Fragment>
              <Divider />
              <List>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: "initial",
                      px: 2.5,
                    }}
                    onClick={() => setSelectedTab("documents")}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 3,
                        justifyContent: "center",
                      }}
                    >
                      <FolderOpenIcon />
                    </ListItemIcon>
                    <ListItemText primary={segmentUploadTitle} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Fragment>
          )}
        </>
      )}
      <Divider />
      {section.title !== "Sport" && (
        <List>
          <ListItem disablePadding sx={{ display: "block" }}>
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: "initial",
                px: 2.5,
              }}
              onClick={() => setSelectedTab("videos")}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: 3,
                  justifyContent: "center",
                }}
              >
                <PlayCircleIcon />
              </ListItemIcon>
              <ListItemText primary={`Videomediathek ${section.title}`} />
            </ListItemButton>
          </ListItem>
        </List>
      )}

      <Divider />
      <ListItem disablePadding sx={{ display: "block" }}>
        <ListItemButton
          sx={{
            minHeight: 48,
            justifyContent: "initial",
            px: 2.5,
          }}
          onClick={() => {
            navigate(`/`);
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 3,
              justifyContent: "center",
            }}
          >
            <KeyboardReturnIcon />
          </ListItemIcon>
          <ListItemText primary="Zurück zur Startseite" />
        </ListItemButton>
      </ListItem>
    </Drawer>
  );
};
